<script lang="ts">
	import "../../assets/css/date-picker.css";
	import { DateInput } from "date-picker-svelte";
	import { createEventDispatcher, onMount } from "svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import type { ActionReturn } from "svelte/action";
	import { dateInputLocale } from "../../../../core/schema/dateInputLocale.js";

	let inputClass = "";
	export { inputClass as class };
	export let id: string;
	export let min: Date | undefined = undefined;
	export let max: Date | undefined = undefined;
	export let value: Date | undefined = undefined;
	export let placeholder = "";
	export let required = false;
	export let withoutLabel = false;
	export let input: HTMLInputElement | null = null;
	export let withoutDayOfTheWeek = false;
	export let disabled = false;
	export let excludedDays: string[] = [];

	const { TENANT_LOCALE } = getGlobalSettings();

	const dispatch = createEventDispatcher<{
		change: `${string}-${string}-${string}` | null;
	}>();

	let hiddenInput: HTMLInputElement | null = null;
	let visible = false;

	onMount(() => {
		input = document.querySelector<HTMLInputElement>(`#${id}`);
		if (!input) {
			throw new Error(`Input with id "${id}" not found`);
		}
		input.readOnly = true;
	});

	function getDateValue(value: Date): `${string}-${string}-${string}` {
		return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
	}

	function onSelect(event: CustomEvent<Date>): void {
		value = event.detail;
		dispatch("change", getDateValue(value));
		setTimeout(() => {
			hiddenInput?.dispatchEvent(new Event("input", { bubbles: true }));
			hiddenInput?.dispatchEvent(new Event("change", { bubbles: true }));
		});
	}

	$: dateValue = value ? getDateValue(value) : "";
	$: now = new Date();
	$: maxDate = max ?? new Date(now.getFullYear() + 100, now.getMonth(), now.getDate(), 23, 59, 59);

	function listenToFormReset(element: HTMLInputElement): ActionReturn {
		const { form } = element;

		function onReset(): void {
			value = undefined;
		}

		form?.addEventListener("reset", onReset);

		return {
			destroy(): void {
				form?.removeEventListener("reset", onReset);
			},
		};
	}

	$: {
		if (visible) {
			input?.scrollIntoView();
		}
	}
</script>

<div class="relative">
	<div class="relative">
		<input
			aria-hidden="true"
			tabindex="-1"
			use:listenToFormReset
			class="pointer-events-none absolute inset-0 opacity-0"
			bind:this={hiddenInput}
			type="text"
			name={id}
			value={dateValue}
			{required}
		/>

		<DateInput
			{id}
			{min}
			bind:visible
			max={maxDate}
			bind:value
			on:select={onSelect}
			format={`${withoutDayOfTheWeek ? "" : (value?.toLocaleDateString(TENANT_LOCALE, { weekday: "long" }) ?? "").concat(" ")}dd. MM. yyyy`}
			browseWithoutSelecting
			closeOnSelection
			{disabled}
			locale={dateInputLocale[TENANT_LOCALE]}
			{placeholder}
			class="transition-colors [&>input]:duration-300 {Boolean(value) || withoutLabel
				? '[&>input]:!text-black'
				: '[&>input]:!text-white'} {withoutLabel
				? '[&>input]:!py-2'
				: '[&>input]:!py-2 [&>input]:!pb-0 [&>input]:!pt-4'} {inputClass}"
			{excludedDays}
		/>

		{#if $$slots.default}
			<label id="{id}-label" for={id} class="label {value ? 'focused-input-label' : 'input-label-no-value'}">
				<slot />{#if required}<span>&nbsp;*</span>{/if}
			</label>
		{/if}
	</div>
</div>
